import React, { useMemo } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import { ChevronDown } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import SDGSection from "./SDGSection";
import { Project } from "@/types";
import { formatNumber } from "@/lib/utils";
import { useProjectsBatchData } from "@/hooks/useProjectsBatchData";
import EcologyPlantHandIcon from "@/icons/EcologyPlantHand";
import AirPollutionCo2 from "@/icons/AirPollutionCo2";
import { TooltipArrow } from "@radix-ui/react-tooltip";
import { getCategoryHexColor } from "@/utils/categoryColors";
import { CategoryType } from "@/types/category";
import { MCD_CUSTOMER_IDS } from "@/constants/customerIds";
import { SILVRETTA_CUSTOMER_ID } from "@/constants/customerIds";

interface DashboardIntroProps {
  customerId: string;
  customerColor?: string;
  projectDeveloperName: string;
  customerName: string;
  uniqueIssuers: {
    name: string;
    logoUrl?: string;
  }[];
  totalContribution?: number;
  projects: Project[];
  projectToBatchIds: Record<string, string[]>;
  batchTitles: Record<string, string>;
}

import { Measure } from "@/types";

type DocumentedMeasurement = Pick<Measure, "category" | "totalNet">;

function aggregateByCategory(
  projects: { id: string; title: string }[],
  projectsMeasurements: Record<
    string,
    { planned: any[]; documented: DocumentedMeasurement[] }
  >
): {
  name: string;
  value: number;
  totalNet: number;
}[] {
  const allCategoriesSet = new Set<string>();
  const categoryTotals: Record<string, number> = {};
  const categoryNetSums: Record<string, number> = {};

  for (const project of projects) {
    const projectId = project.id;
    const measurements = projectsMeasurements[projectId];

    if (!measurements) {
      continue;
    }

    const planned = measurements.planned || [];
    const documented = measurements.documented || [];

    // If there are documented measures, use those
    if (documented.length > 0) {
      const byCategory = documented.reduce((acc, item) => {
        const cat = item.category;
        if (!acc[cat]) acc[cat] = [];
        acc[cat].push(item);
        return acc;
      }, {} as Record<string, DocumentedMeasurement[]>);

      for (const cat of Object.keys(byCategory)) {
        allCategoriesSet.add(cat);
        const catArr = byCategory[cat];

        categoryTotals[cat] = (categoryTotals[cat] || 0) + catArr.length;

        const sumNet = catArr.reduce(
          (acc, doc) =>
            acc + (typeof doc.totalNet === "number" ? doc.totalNet : 0),
          0
        );
        categoryNetSums[cat] = (categoryNetSums[cat] || 0) + sumNet;
      }
    }
    // If no documented measures, use planned measures
    else if (planned.length > 0) {
      for (const measure of planned) {
        if (!measure.category) continue;

        allCategoriesSet.add(measure.category);
        const budget = measure.plannedBudget || 0;

        categoryTotals[measure.category] =
          (categoryTotals[measure.category] || 0) + 1;
        categoryNetSums[measure.category] =
          (categoryNetSums[measure.category] || 0) + budget;
      }
    }
  }

  return Array.from(allCategoriesSet)
    .map((category) => ({
      name: category,
      value: categoryTotals[category] || 0,
      totalNet: categoryNetSums[category] || 0,
    }))
    .sort((a, b) => b.totalNet - a.totalNet);
}

const DashboardIntro: React.FC<DashboardIntroProps> = ({
  customerId,
  customerColor = "#00383c",
  projectDeveloperName,
  customerName,
  uniqueIssuers,
  totalContribution = 0,
  projects,
  projectToBatchIds,
  batchTitles,
}) => {
  const { t, language } = useLanguage();

  // Determine which heading to show based on customer ID
  const getHeading = () => {
    if (MCD_CUSTOMER_IDS.includes(customerId)) {
      const headingText = t("dashboard.intro.headingMcDonalds").replace(
        "{customerName}",
        customerName
      );
      return (
        <>
          {headingText.split(customerName).map((part, index, array) => (
            <React.Fragment key={index}>
              {part}
              {index < array.length - 1 && (
                <span style={{ color: customerColor }}>{customerName}</span>
              )}
            </React.Fragment>
          ))}
        </>
      );
    } else if (SILVRETTA_CUSTOMER_ID === customerId) {
      const headingText = t("dashboard.intro.headingSilvretta").replace(
        "{customerName}",
        customerName
      );
      return (
        <>
          {headingText.split(customerName).map((part, index, array) => (
            <React.Fragment key={index}>
              {part}
              {index < array.length - 1 && (
                <span style={{ color: customerColor }}>{customerName}</span>
              )}
            </React.Fragment>
          ))}
        </>
      );
    } else {
      return (
        <>
          <span style={{ color: customerColor }}>{customerName}</span>{" "}
          {t("dashboard.intro.heading").replace(
            "{projectDeveloperName}",
            projectDeveloperName
          )}
        </>
      );
    }
  };

  // Calculate total projects from the actual projects data
  const totalProjects = projects.length;

  // Get batch data for all projects
  const { projectsMeasurements } = useProjectsBatchData(
    projects,
    projectToBatchIds,
    batchTitles
  );

  // Calculate total measures
  const totalMeasuresCount = useMemo(() => {
    return projects.reduce((total, project) => {
      const measurements = projectsMeasurements[project.id];
      if (!measurements) return total;

      const documentedCount = measurements.documented.length;
      const plannedCount = measurements.planned.length;

      // Prioritize documented measures, fallback to planned if none
      return total + (documentedCount > 0 ? documentedCount : plannedCount);
    }, 0);
  }, [projects, projectsMeasurements]);

  // Get top 3 measures by totalNet
  const topMeasuresList = useMemo(() => {
    const aggregated = aggregateByCategory(projects, projectsMeasurements);
    return aggregated.slice(0, 3).map((measure) => {
      const categoryName = measure.name;
      return {
        name: categoryName,
        displayName: t(`categories.${categoryName}`),
        description: t(`categories.descriptions.${categoryName}`),
        color: getCategoryHexColor(categoryName as CategoryType),
      };
    });
  }, [projects, projectsMeasurements, t]);

  // These values would normally come from API data
  // In a real implementation, you'd pass these values from the Dashboard component
  const forestArea = formatNumber(
    projects.reduce((acc, project) => acc + project.area, 0) / 10000
  );

  const scrollToProjects = () => {
    const projectSection = document.getElementById("supported-projects");

    if (projectSection) {
      // Calculate offset to scroll slightly above the top of the project section
      const offset =
        projectSection.getBoundingClientRect().top + window.scrollY - 100; // Adjusted by 50 pixels
      window.scrollTo({
        top: offset,
        behavior: "smooth",
      });
    }
  };

  return (
    <div className="py-6 mb-16">
      {/* Main content section */}
      <div className="py-12 grid grid-cols-1 lg:grid-cols-[1.25fr_1fr] gap-4 lg:gap-[11%]">
        {/* Left column - Text content */}
        <div>
          <h2 className="text-4xl font-bold leading-tight mb-6 break-words">
            {getHeading()}
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
            {t("dashboard.intro.description")} {projectDeveloperName}{" "}
            {t("dashboard.intro.projects")}{" "}
            {t("dashboard.intro.descriptionEnd")}
          </p>

          <hr className="my-8 border-gray-200" />

          <div className="flex flex-col lg:flex-row gap-8">
            <div className="lg:w-1/2">
              <SDGSection />
            </div>
            <div className="lg:w-1/2">
              {uniqueIssuers.length > 0 && (
                <div className="mt-4">
                  <div className="flex flex-wrap justify-start gap-2 mb-3">
                    {/* Render unique issuers */}
                    {uniqueIssuers.map((issuer, index) => (
                      <img
                        key={index}
                        src={issuer.logoUrl}
                        alt={issuer.name}
                        className="w-12 h-12 rounded-lg object-contain"
                      />
                    ))}
                    {/* Add ISO logo */}
                    <img
                      src="/ISO_Logo.png"
                      alt="ISO Logo"
                      className="w-12 h-12 rounded-lg object-contain"
                    />
                  </div>
                  <p className="text-gray-600 text-sm">
                    {t("dashboard.intro.certification")}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Right column - Stats cards */}
        <div className="flex flex-col gap-4">
          {/* Header text */}
          <p className="text-xl text-gray-600 max-w-3xl whitespace-pre-line">
            {t("dashboard.intro.statsHeader").replace(
              "{projectDeveloperName}",
              projectDeveloperName
            )}
          </p>

          {/* Main contribution card */}
          <Card
            className="rounded-[20px] text-white w-full cursor-pointer"
            style={{ backgroundColor: customerColor }}
            onClick={scrollToProjects}
          >
            <CardContent className="p-6 flex items-center justify-between">
              <div>
                <h3 className="text-3xl font-bold mb-1 font-mono">
                  {new Intl.NumberFormat(
                    language === "en" ? "en-US" : "de-DE",
                    {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    }
                  ).format(totalContribution / 1000)}{" "}
                  tCO<sub>2</sub>e
                </h3>
                <p>{t("dashboard.intro.contributionAmount")}</p>
              </div>
              <div className="bg-white/20 p-4 rounded-full">
                <AirPollutionCo2 />
              </div>
            </CardContent>
          </Card>

          {/* Grid of stat cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Projects card */}
            <Card
              className="bg-white rounded-[20px] border border-gray-200 cursor-pointer"
              onClick={scrollToProjects}
            >
              <CardContent className="p-6">
                <h3
                  className="text-3xl font-bold mb-1"
                  style={{ color: customerColor, fontFamily: "monospace" }}
                >
                  {totalProjects}
                </h3>
                <p className="text-gray-600">
                  <b>{t("dashboard.intro.forestProjects")}</b>{" "}
                  {language === "en" && t("dashboard.intro.supported")}
                </p>
              </CardContent>
            </Card>

            {/* Forest Area card */}
            <Card
              className="bg-white rounded-[20px] border border-gray-200 cursor-pointer"
              onClick={scrollToProjects}
            >
              <CardContent className="p-6">
                <h3
                  className="text-3xl font-bold mb-1 font-mono"
                  style={{ color: customerColor }}
                >
                  {forestArea} ha
                </h3>
                <p className="text-gray-600">
                  <b>{t("dashboard.intro.forestArea")}</b>{" "}
                  {language === "en" && t("dashboard.intro.supported")}
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Measures card */}
          <Card
            className="bg-white rounded-[20px] border border-gray-200 mb-12 cursor-pointer"
            onClick={scrollToProjects}
          >
            <CardContent className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 grid-rows-[auto_auto] gap-6 relative">
                {/* Vertical divider - only show on md and up */}
                <div className="absolute left-1/2 top-0 bottom-0 w-px bg-gray-200 -translate-x-1/2 hidden md:block"></div>

                {/* Top Left: Top Measures List */}
                <div className="flex flex-col gap-5 relative z-10 pt-4">
                  {topMeasuresList.map((measure, index) => (
                    <TooltipProvider key={index} delayDuration={0}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <h3
                            className="text-lg font-bold font-mono uppercase"
                            style={{
                              color: measure.color,
                              lineHeight: "1",
                            }}
                          >
                            {measure.displayName}
                          </h3>
                        </TooltipTrigger>
                        <TooltipContent
                          sideOffset={5}
                          className="bg-black text-white border-none rounded-xl"
                        >
                          <TooltipArrow />
                          <p className="max-w-xs">{measure.description}</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  ))}
                  {/* Top Measures Label - only show on mobile */}
                  <div className="flex items-start md:hidden">
                    <p className="text-gray-600">
                      <b>{t("dashboard.intro.topMeasures")}</b>{" "}
                      {language === "en" && t("dashboard.intro.supported")}
                    </p>
                  </div>
                </div>

                {/* Horizontal divider - only show on mobile */}
                <div className="h-px bg-gray-200 md:hidden col-span-1"></div>

                {/* Top Right: Total Measures Count */}
                <div className="flex flex-col items-start justify-end relative z-10 pt-4 md:pl-6">
                  <div
                    className="absolute top-0 right-0 bg-emerald-100 p-4 rounded-full"
                    style={{ backgroundColor: `${customerColor}20` }}
                  >
                    <EcologyPlantHandIcon color={customerColor} />
                  </div>
                  <h3
                    className="text-3xl font-bold font-mono"
                    style={{ color: customerColor }}
                  >
                    {totalMeasuresCount}
                  </h3>
                </div>

                {/* Bottom Left: Top Measures Label - only show on desktop */}
                <div className="hidden md:flex items-start relative z-10">
                  <p className="text-gray-600">
                    <b>{t("dashboard.intro.topMeasures")}</b>{" "}
                    {language === "en" && t("dashboard.intro.supported")}
                  </p>
                </div>

                {/* Bottom Right: Total Measures Label */}
                <div className="flex items-start relative z-10 md:pl-6">
                  <p className="text-gray-600">
                    <b>{t("dashboard.intro.totalMeasures")}</b>{" "}
                    {language === "en" && t("dashboard.intro.supported")}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Explore projects button */}
      <div className="flex justify-center">
        <button
          onClick={scrollToProjects}
          className="flex flex-col items-center gap-1 bg-transparent border-none shadow-none cursor-pointer focus:outline-none"
          style={{ color: customerColor }}
        >
          <span className="text-lg font-medium" style={{ color: "#5B6C67" }}>
            {t("dashboard.intro.exploreProjects")}
          </span>
          <ChevronDown className="h-6 w-6" style={{ color: customerColor }} />
        </button>
      </div>
    </div>
  );
};

export default DashboardIntro;
